import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import { isRequired } from 'components/Questionnaires/validators';

export const QuestionGiinField = ({ intl, blockId, question, className, onChange }) => {
  const fieldLength = 16;
  const fieldSeperator = [5, 10, 12];

  const defaultValue = () => {
    let value = question.answer ?? question.default ?? '';
    if (!value) {
      return (new Array(fieldLength)).fill({ value: '', changed: false });
    }
    value = value.replace('.', '');
    if (value.length < fieldLength) {
      value = value.padEnd(fieldLength);
    } else {
      value = value.substring(0, fieldLength - 1);
    }
    return (new Array(fieldLength)).fill((v, i) => ({ value: (value[i] && value[i] !== ' ' ? value[i] : ''), changed: false }));
  };

  const [inputValue, setInputValue] = useState(defaultValue);

  const inputRef = [...Array(fieldLength).keys()].map(x => useRef(null));
  const numRegex = /^["'0-9'"]*$/;
  const lettersRegex = /[A-Za-z]/;
  const numLettersRegex = /[A-Za-z0-9]/;

  const toString = (value) => {
    const source = value ?? inputValue;
    let giinString = '';
    source.forEach((element) => {
      giinString = giinString.concat(element.value).toUpperCase();
    });

    return giinString;
  };

  function onKeyHandler(event, index) {
    if (event === null) {
      return;
    }

    if (event.shiftKey || event.keyCode === 9 || event.keyCode === 16) {
      return;
    }

    if (event.keyCode === 8) {
      if (index !== 0) {
        const previous = inputRef[index - 1];
        if (previous) {
          previous.current.focus();
        }
      }
    } else {
      const next = inputRef[index + 1];
      if (next && event.target.value !== '') {
        next.current.focus();
      }
    }
  }

  function onInputChange(event, fieldIndex) {
    if (event === null || event.target === null || fieldIndex === null) {
      return;
    }

    if (event.target.value) {
      if ((!numLettersRegex.test(event.target.value) && fieldIndex <= 5)
        || (!numRegex.test(event.target.value) && (fieldIndex >= 6 && fieldIndex <= 10))
        || (!lettersRegex.test(event.target.value) && (fieldIndex >= 11 && fieldIndex <= 12))
        || (!numRegex.test(event.target.value) && fieldIndex >= 13)
        || event.target.value.length > 1) {
        return;
      }
    }

    const newInputValue = inputValue.map((fieldInput, i) => (i === fieldIndex ? {
      changed: true,
      value: event.target.value
    } : fieldInput));

    setInputValue(newInputValue);
    onChange(question, toString(newInputValue));
  }

  useEffect(() => {
    if (!inputValue.every(v => v.value === '')) {
      onChange(question, toString());
    }
  }, []);

  return (
    <div id={ question.id } className={ className }>
      { Array.from({ length: fieldLength }, (element, index) => (
        <span key={ `item-${ index }` }>
          <input
            id={ `giin-field-${ index + 1 }` }
            name={ question.id }
            disabled={ question.disabled }
            className={ `giin-box${ question.isValid === false ? ' error' : '' }` }
            ref={ inputRef[index] ? inputRef[index] : '' }
            maxLength={ 1 }
            type="text"
            autoComplete="off"
            onKeyUp={ e => onKeyHandler(e, index) }
            onChange={ e => onInputChange(e, index) }
            value={ inputValue[index] && inputValue[index].value ? inputValue[index].value : '' }
            required={ isRequired(question) }
            aria-label={ intl.formatMessage({ id: question.label }) }
            aria-required={ isRequired(question) }
            aria-describedby={ question.isValid === false ? (`${ blockId }-${ question.id }-error`) : undefined }
          />
          { fieldSeperator && (fieldSeperator.indexOf(index) >= 0) && <span className="box-input-seperator giin-seperator">-</span>}
        </span>
      )) }
    </div>
  );
};

QuestionGiinField.displayName = 'QuestionGiinField';

QuestionGiinField.propTypes = {
  intl: PropTypes.shape().isRequired,
  blockId: PropTypes.string.isRequired,
  question: PropTypes.shape().isRequired,
  className: PropTypes.string,
  onChange: PropTypes.func.isRequired
};

QuestionGiinField.defaultProps = {
  className: ''
};

export default QuestionGiinField;
